import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatUSD } from "@/lib/format";
import { api } from "@/lib/api";

const MEMBER_MIN = 199;
const MEMBER_OFF = 25;

const NYC_ZIP_RANGES = [[10001, 10282], [10301, 10314], [10451, 10475], [11004, 11005],
  [11101, 11106], [11201, 11256], [11351, 11436], [11691, 11697]];
const isNycZip = (z) => /^\d{5}$/.test((z || "").trim()) &&
  NYC_ZIP_RANGES.some(([a, b]) => +z >= a && +z <= b);
const isUsMobile = (p) => /^\+1[2-9]\d{9}$/.test((p || "").trim());

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, subtotal } = useCart();
  const { user } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const [step, setStep] = useState("cart");
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", province: "", zip: "" });
  const [leadEmail, setLeadEmail] = useState("");
  const [leadSaved, setLeadSaved] = useState(false);

  const saveLead = async () => {
    try {
      await api.post("/leads", { email: leadEmail, zip: form.zip });
      setLeadSaved(true);
      toast.success("Noted — we'll write the day we reach your area.");
    } catch {
      toast.error("Please enter a valid email address.");
    }
  };

  const memberSaving = user && subtotal >= MEMBER_MIN ? MEMBER_OFF : 0;
  const total = subtotal - memberSaving;

  useEffect(() => {
    if (step === "shipping" && user) {
      api.get("/auth/me").then((r) => {
        const u = r.data;
        const s = u.default_shipping || {};
        setForm((f) => ({
          name: f.name || s.name || u.name || "",
          email: f.email || s.email || u.email || "",
          phone: f.phone || s.phone || u.mobile || "",
          address: f.address || s.address || "",
          city: f.city || s.city || "",
          province: f.province || s.province || "",
          zip: f.zip || s.zip || "",
        }));
      }).catch(() => {});
    }
  }, [step, user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checkout = async () => {
    if (!form.name || !form.email || !form.address || !form.city || !form.zip) {
      toast.error("Please fill in your name, email, address, city and ZIP.");
      return;
    }
    if (!isNycZip(form.zip)) {
      toast.error("We deliver within New York City only — that ZIP code is outside our service area.");
      return;
    }
    if (!isUsMobile(form.phone)) {
      toast.error("Please enter a valid US mobile number, e.g. +12125550123 — our delivery team calls before arriving.");
      return;
    }
    setCheckingOut(true);
    try {
      const res = await api.post("/checkout", {
        items: items.map((i) => ({ slug: i.slug, qty: i.qty, vid: i.vid })),
        origin_url: window.location.origin,
        shipping: { ...form, country: "United States", country_code: "US" },
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Checkout could not be started. Please try again.");
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col bg-bone"
          >
            <div className="flex items-center justify-between border-b border-black/10 px-8 py-6">
              <h2 className="font-serif text-2xl tracking-tight">Your cart</h2>
              <button
                data-testid="cart-close-button"
                onClick={() => setOpen(false)}
                className="rounded-full p-2 transition-colors duration-300 hover:bg-sand"
                aria-label="Close cart"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {items.length === 0 ? (
                <p data-testid="cart-empty-message" className="mt-16 text-center text-sm font-light text-ash">
                  Your cart is empty. The good pieces are waiting.
                </p>
              ) : (
                <ul className="space-y-8">
                  {items.map((i) => (
                    <li key={i.key} data-testid={`cart-item-${i.key}`} className="flex gap-5">
                      <div className="h-24 w-20 shrink-0 overflow-hidden bg-sand">
                        <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-3">
                          <p className="font-serif text-lg leading-tight">
                            {i.name}
                            {i.variantName && <span className="block text-xs uppercase tracking-[0.15em] text-ash">{i.variantName}</span>}
                          </p>
                          <button
                            data-testid={`cart-remove-${i.key}`}
                            onClick={() => remove(i.key)}
                            className="text-ash transition-colors duration-300 hover:text-charcoal"
                            aria-label={`Remove ${i.name}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <p className="mt-1 text-sm text-ash">{formatUSD(i.price)}</p>
                        <div className="mt-auto flex items-center gap-4 pt-2">
                          <button
                            data-testid={`cart-qty-minus-${i.key}`}
                            onClick={() => setQty(i.key, i.qty - 1)}
                            className="rounded-full border border-charcoal/20 p-1.5 transition-colors duration-300 hover:bg-charcoal hover:text-bone"
                            aria-label="Decrease quantity"
                          >
                            <Minus size={12} />
                          </button>
                          <span data-testid={`cart-qty-${i.key}`} className="w-4 text-center text-sm">{i.qty}</span>
                          <button
                            data-testid={`cart-qty-plus-${i.key}`}
                            onClick={() => setQty(i.key, i.qty + 1)}
                            className="rounded-full border border-charcoal/20 p-1.5 transition-colors duration-300 hover:bg-charcoal hover:text-bone"
                            aria-label="Increase quantity"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {items.length > 0 && step === "cart" && (
              <div className="border-t border-black/10 px-8 py-6">
                <div className="mb-5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-[0.2em] text-ash">Subtotal</span>
                    <span data-testid="cart-subtotal" className="font-serif text-2xl">{formatUSD(subtotal)}</span>
                  </div>
                  {memberSaving > 0 && (
                    <div className="flex items-center justify-between text-moss" data-testid="member-saving">
                      <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]"><BadgePercent size={13} /> Member saving</span>
                      <span className="font-serif text-lg">-{formatUSD(memberSaving)}</span>
                    </div>
                  )}
                  {user && subtotal < MEMBER_MIN && (
                    <p className="text-xs font-light text-ash" data-testid="member-nudge">
                      Members save {formatUSD(MEMBER_OFF)} on orders over {formatUSD(MEMBER_MIN)} — you're {formatUSD(MEMBER_MIN - subtotal)} away.
                    </p>
                  )}
                </div>
                <button
                  data-testid="checkout-button"
                  onClick={() => setStep("shipping")}
                  className="group flex w-full items-center justify-center gap-3 rounded-full bg-charcoal py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss"
                >
                  Proceed to Checkout
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <p className="mt-4 text-center text-xs font-light text-ash">
                  Secure payment by Stripe — cards, Apple Pay, Google Pay.
                </p>
              </div>
            )}
            {items.length > 0 && step === "shipping" && (
              <div className="border-t border-black/10 px-8 py-6">
                <p className="mb-4 text-xs uppercase tracking-[0.2em] text-ash">Delivery details — New York</p>
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="ship-name" placeholder="Full name" value={form.name} onChange={set("name")}
                    className="col-span-2 rounded-md border border-charcoal/20 bg-transparent px-4 py-2.5 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
                  <input data-testid="ship-email" type="email" placeholder="Email" value={form.email} onChange={set("email")}
                    className="col-span-2 rounded-md border border-charcoal/20 bg-transparent px-4 py-2.5 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
                  <input data-testid="ship-address" placeholder="Street address" value={form.address} onChange={set("address")}
                    className="col-span-2 rounded-md border border-charcoal/20 bg-transparent px-4 py-2.5 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
                  <input data-testid="ship-city" placeholder="City" value={form.city} onChange={set("city")}
                    className="rounded-md border border-charcoal/20 bg-transparent px-4 py-2.5 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
                  <input data-testid="ship-zip" placeholder="ZIP (NYC only)" value={form.zip} onChange={set("zip")}
                    className="rounded-md border border-charcoal/20 bg-transparent px-4 py-2.5 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
                  <input data-testid="ship-phone" placeholder="US mobile — +1…" value={form.phone} onChange={set("phone")}
                    className="col-span-2 rounded-md border border-charcoal/20 bg-transparent px-4 py-2.5 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
                </div>
                {form.zip && !isNycZip(form.zip) && (
                  <div data-testid="zip-error" className="mt-3">
                    <p className="text-xs font-light text-moss">
                      That ZIP is outside our service area — we deliver within New York City only.
                    </p>
                    {leadSaved ? (
                      <p data-testid="lead-saved" className="mt-3 text-xs font-light text-charcoal">
                        Noted — we'll email you the day we deliver to {form.zip}.
                      </p>
                    ) : (
                      <div className="mt-3 flex gap-2">
                        <input
                          data-testid="lead-email"
                          type="email"
                          placeholder="Your email"
                          value={leadEmail}
                          onChange={(e) => setLeadEmail(e.target.value)}
                          className="flex-1 rounded-md border border-charcoal/20 bg-transparent px-3 py-2 text-xs outline-none placeholder:text-ash/60 focus:border-charcoal"
                        />
                        <button
                          data-testid="lead-notify-button"
                          onClick={saveLead}
                          className="rounded-full bg-charcoal px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-bone transition-colors duration-300 hover:bg-moss"
                        >
                          Notify me
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {form.phone && !isUsMobile(form.phone) && (
                  <p data-testid="phone-error" className="mt-2 text-xs font-light text-moss">
                    US mobile numbers only, starting with +1 — our delivery team calls before arriving.
                  </p>
                )}
                <button
                  data-testid="pay-now-button"
                  onClick={checkout}
                  disabled={checkingOut}
                  className="group mt-5 flex w-full items-center justify-center gap-3 rounded-full bg-charcoal py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss disabled:opacity-60"
                >
                  {checkingOut ? "Taking you to payment..." : `Pay ${formatUSD(total)} securely`}
                  {!checkingOut && <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />}
                </button>
                <button
                  data-testid="back-to-cart-button"
                  onClick={() => setStep("cart")}
                  className="mt-3 w-full text-center text-xs uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:text-charcoal"
                >
                  Back to cart
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
